import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import ModalConfirm from "components/Common/ModalConfirm";
import { useRole } from "components/Hooks/UserHooks";
import CountUp from "react-countup";
import ModalPreview from "components/Common/ModalPreview";
import noImage1 from "../../../../assets/images/no-image-icon.png";
import UtilityForm from "./UtilityForm";
import { Link } from "react-router-dom";
import TooltipCustom from "components/Common/TooltipCustom";
import facilitiesApi from "api/facilitiesApi";
import { getListUtility, putFacilityUpdateBarStatus } from "store/facility/thunk";

registerLocale("en", en);
registerLocale("ko", ko);

export interface ISearch {
  page: number;
  limit: number;
  keyword: string;
}

export interface IFacility {
  id?: number,
  icon?: string,
  name?: string
}

export interface IResFacilites {
  list?: IFacility[],
  meta?: {
    total: number,
    page: number,
    limit: number
  },
}

const DEFAULT_DATA_SEARCH = {
  page: 1,
  limit: 100,
  keyword: "",
};

export interface IProps {
  barID?: any
}

const UtilitiesList = ({
  barID,
}: IProps) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const noImage = useMemo(() => {
    return noImage1;
  }, []);

  const [queryFacility, setQueryFacility] = useState<ISearch>(DEFAULT_DATA_SEARCH)

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [isFacilityLoading, setIsFacilityLoading] = useState<boolean>(false);
  const [itemToEdit, setItemToEdit] = useState<any>(false);
  const [linkImage, setLinkImage] = useState<string>("");
  const [formatData, setFormatData] = useState<any>([]);
  
  const loadTableData = async (queryData?: ISearch, lstFacilitiesActive?: any, isSearch=false) => {
    if(!barID) return;

    setIsFacilityLoading(true);
    try { 
      const resDetail: any = await getListUtility({
        ...queryFacility,
        bar_id: barID
      });

      if (resDetail?.data?.list) {
        setFormatData(resDetail?.data?.list);
        setIsFacilityLoading(false);
      }else {
        setIsFacilityLoading(false);
      }
    } catch (error) {
      setIsFacilityLoading(false);
      console.log(error)
    }
  }

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const handleOnConfirmUpdate = async (idFacility: string, value: any) => {
    if (!barID || !idFacility) return;

    try {
      setIsConfirmLoading((_prev) => true);
      const data = {
        id: idFacility,
        bar_id: barID,
        bar_status: value ? 1 : 0
      };

      const response: any = await putFacilityUpdateBarStatus(data)
      if (response?.code === 200) {
        loadTableData(queryFacility);

        setIsConfirmLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response?.message}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        Cell: ({ ...props }) => {
          return (
            <div>
              {formatData?.length -
                ((queryFacility?.page - 1) * queryFacility?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Active"),
        accessor: "status",
        thWidth: 80,
        filterable: false,
        sortable: false,
        thClass: "text-center",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={item?.bar_status}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate(item?.id, !!value);
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Image"),
        accessor: "icon",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        thWidth: 170,
        Cell: (cell: any) =>
          cell?.value ? (
            <>
              <div className="w-100 text-center " style={{ minWidth: "150px" }}>
                <div
                  style={{ width: "25px" }}
                  className="m-auto cursor-pointer"
                  onClick={() => setLinkImage((prev) => cell?.value)}
                >
                  <img
                    src={cell?.value}
                    className="flex-shrink-0 w-100"
                    alt="thumbnail"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = noImage;
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            ""
          ),
      },
      {
        Header: t("Name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">
                {cell?.value?.en}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Name (Korean)"),
        accessor: "name_korean",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;

          return (
            <>
              <div className="cursor-pointer">
                {item?.name?.ko}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return item?.type === "customize" && (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ width: "150px", justifyContent: "center" }}
            >
              <TooltipCustom
                title={t("Button Update")}
                id={`update-md-${barID}`}
              >
                <li className="list-inline-item" id={`update-md-${barID}`}>
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setItemToEdit(item);
                      setIsOpen(false);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom "></i>
                  </Link>
                </li>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete")}
                id={`delete-md-${barID}`}
              >
                <li className="list-inline-item" id={`delete-md-${barID}`}>
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setItemToDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </li>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, formatData?.length]
  );

  const handleChangePage = useCallback((page: any) => {
    setQueryFacility({ ...queryFacility, page: page + 1 });
    loadTableData({ ...queryFacility, page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseImageClick = () => {
    setLinkImage((_prev) => "");
  };
  // Begin::Delete

  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await facilitiesApi?.deleteFacility(id);
      if (response?.data) {
        loadTableData();
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const triggerRefresh = () => {
    setItemToEdit(null);
    onCloseClick();
    loadTableData();
  };

  useEffect(() => {
    loadTableData();
  }, [])

  // End::Delete
  useEffect(() => {
    document.title = `${t("Utilities")} - ${t("Store List")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="mt-3">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={formatData?.length || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                    <div className="col-sm-auto text-end">
                      <button
                        type="button"
                        className="btn btn-success me-1"
                        onClick={() => setIsOpen((_prev) => true)}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Button Create Utility")}
                      </button>
                    </div>
                  </Row>
                </CardHeader>
                {/* <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={t("Name")}
                          isShow={!!keywordSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Name")}...`}
                            value={keywordSearch}
                            onChange={(e) => handleChangeKeyword(e?.target?.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col sm={12} md={7} xl={7} xxl={7} className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-2">
                        <div>
                          <button
                            type="button"
                            className="btn btn-success mb-2 me-3 mb-sm-0"
                            onClick={(e) => { e.preventDefault(); (handleSetFacilityForBar()) }}
                            disabled={isConfirmLoading || countRowSelected <= 0}
                          >
                            {countRowSelected > 0 && (
                              <span className="position-absolute topbar-badge badge rounded-pill bg-danger"
                                style={{ transform: 'translate(0%, -70%)' }}
                              >
                                {formatNumberWithCommas(countRowSelected)}
                                <span className="visually-hidden">total facilities selected</span>
                              </span>)}
                            <i className="ri-checkbox-circle-line align-bottom me-1"></i>{" "}
                            {t('Update Facilities')}
                          </button>
                        </div>
                      </Col>
                      <Col
                        sm={12}
                        md={9}
                        xl={9}
                        xxl={9}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-4 mt-md-2"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div> */}
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={formatData}
                      customPageSize={queryFacility.limit}
                      customPageIndex={queryFacility.page - 1}
                      totalRecords={formatData?.length}
                      customPageCount={Math.ceil(
                        Number(formatData?.length) / Number(queryFacility.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      // sorting={{
                      //   sort_by: queryFacility.sort_by,
                      //   order_by: queryFacility.order_by,
                      // }}
                      // handleChangeSorting={handleChangeSorting}
                      isLoading={isFacilityLoading}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <Modal
          isOpen={!!itemToEdit?.id || isOpen}
          centered={true}
          size="l"
          scrollable={false}
        >
          <ModalHeader toggle={onCloseClick}>
            <>
              {!!itemToEdit?.id
                ? t("Button Update Utility")
                : t("Button Create Utility")}
            </>{" "}
            <>
              {itemToEdit?.title &&
                (" - " +
                  (itemToEdit?.name || "") ||
                  "")}
            </>
          </ModalHeader>
          <ModalBody>
            <UtilityForm
              isModal={true}
              id={String(itemToEdit?.id || "")}
              isCopy={!!itemToEdit?.isCopy}
              triggerRefresh={triggerRefresh}
              setLinkImage={setLinkImage}
              rowSelected={itemToEdit}
              barId={barID}
            />
          </ModalBody>
        </Modal>

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete Utility")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={"IMAGE"}
          isOpen={!!linkImage}
          url={linkImage}
          onClose={() => onCloseImageClick()}
        />

        {/* <Modal
          isOpen={isModalArtist}
          id="artist-modal"
          centered
          size="xl"
          scrollable={true}
          // toggle={onCloseDetailCategoryClick}
          keyboard={true}
        >
          <ModalHeader toggle={()=> setIsModalArtist(false)}>
            {t("Keywords")}
          </ModalHeader>
          <ModalBody className="text-center">
            <div
              className="row g-4"
              style={{ minHeight: "calc(100dvh - 200px)" }}
            >
              <TrendIndexKeyword
                detailCategory={detailCategoryLoading || undefined}
                handleRefreshListCategory={handleRefetchCategoriesTrendIndex}
              />
            </div>
          </ModalBody>
        </Modal> */}
      </div>
    </React.Fragment>
  );
};

export default UtilitiesList;
